import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useNavigate } from 'react-router';
import { AppContext } from '../window';
import { useTranslation } from '../i18n';
import OneTapButtons from '../psp/OneTapButtons';
import { fetchOneTapOffer, OneTapOffer } from '../psp/oneTap';

interface TopUpContextType {
  /**
   * Where an out-of-credits action sends the member: the top-up modal with
   * "Pay <amount> with <method>" / "Other options" when they have a one-tap
   * offer, else (first-time buyers included) the payment page as before.
   */
  goToPayment: () => Promise<void>;
}

const TopUpContext = createContext<TopUpContextType | undefined>(undefined);

export const TopUpProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const { user } = useContext(AppContext);
  const navigate = useNavigate();
  const [offer, setOffer] = useState<OneTapOffer | null>(null);
  const hasPaidBefore = Boolean(user?.credentials?.lastTransaction);

  const goToPayment = useCallback(async () => {
    const found = hasPaidBefore ? await fetchOneTapOffer() : null;
    if (found) {
      setOffer(found);
    } else {
      navigate('/credits');
    }
  }, [hasPaidBefore, navigate]);

  const close = useCallback(() => setOffer(null), []);
  const otherOptions = useCallback(() => {
    setOffer(null);
    navigate('/credits');
  }, [navigate]);

  const value = useMemo(() => ({ goToPayment }), [goToPayment]);

  return (
    <TopUpContext.Provider value={value}>
      {children}
      <Modal show={offer !== null} onHide={close} centered className="top-up-modal">
        <Modal.Header className="border-0 pb-0" closeButton>
          <Modal.Title className="w-100 text-center">{t('payment.top_up_title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 py-3 text-center">
          <p className="mb-3">{t('payment.top_up_description', { credits: offer?.credits ?? '' })}</p>
          {offer && <OneTapButtons offer={offer} onOtherOptions={otherOptions} />}
        </Modal.Body>
      </Modal>
    </TopUpContext.Provider>
  );
};

/**
 * See TopUpContextType::goToPayment. Outside a TopUpProvider (the standalone
 * slider and conversation apps, tests) it is the plain payment-page link.
 */
export const useTopUp = (): TopUpContextType => {
  const context = useContext(TopUpContext);
  const navigate = useNavigate();
  const fallback = useMemo(() => ({ goToPayment: async () => { navigate('/credits'); } }), [navigate]);
  return context ?? fallback;
};
